import { PortfolioModel } from "@modelve/api/models/portfolioModels";
import { ResourceModel, UserRole } from "@modelve/api/models/resourceModels";
import { TenantModel } from "@modelve/api/models/tenantModels";
import { NavigateFunction } from "react-router";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const recentItemLimit = 5;

export interface RecentPortfolio {
  id: string;
  name: string;
  date: Date;
}

export interface UserStoreState {
  userId?: string;
  tenantId?: string;
  tenantName?: string;
  tenantRole?: UserRole;
  portfolioId?: string;
  portfolioName?: string;
  portfolioRole?: UserRole;
  isDemo?: boolean;
  recentPortfolios: RecentPortfolio[];
  updateTenantName: (tenant: TenantModel) => void;
  updatePortfolioName: (portfolio: PortfolioModel) => void;
  loadResource: (resource: ResourceModel) => void;
  addRecentPortfolio: (id: string, name: string) => void;
  validateRecent: (
    navigate: NavigateFunction,
    resources: ResourceModel[],
  ) => Promise<void>;
}

export const initialState = {
  userId: undefined,
  tenantId: undefined,
  tenantName: undefined,
  tenantRole: undefined,
  portfolioId: undefined,
  portfolioName: undefined,
  portfolioRole: undefined,
  recentPortfolios: [],
};

export const useUserStore = create<UserStoreState>()(
  persist(
    (set, get) => ({
      ...initialState,
      addRecentPortfolio: (id: string, name: string) => {
        let recent = get().recentPortfolios.map((x) => ({ ...x }));
        const existed = recent.find((portfolio) => portfolio.id === id);

        if (existed) {
          existed.date = new Date();
          existed.name = name;
          recent.sort((a, b) => b.date.getTime() - a.date.getTime());
        } else {
          if (recent.length === recentItemLimit) recent.pop();

          recent = [
            {
              id: id,
              name: name,
              date: new Date(),
            },
          ].concat(recent);
        }

        set({
          ...get(),
          recentPortfolios: recent,
        });
      },
      loadResource: (resource: ResourceModel) =>
        set({
          tenantId: resource.tenantId,
          tenantName: resource.tenantName,
          tenantRole: resource.tenantRole,
          portfolioId: resource.portfolioId,
          portfolioName: resource.portfolioName,
          portfolioRole: resource.portfolioRole,
          isDemo: resource.isDemo,
        }),
      updateTenantName: (tenant: TenantModel) =>
        set((state) => ({ ...state, tenantName: tenant.name })),
      updatePortfolioName: (portfolio: PortfolioModel) =>
        set((state) => ({ ...state, portfolioName: portfolio.name })),
      validateRecent: async (navigate, resources) => {
        const portfolios = resources.filter(
          (resource) => !!resource.portfolioId,
        );

        const recentPortfolios = get().recentPortfolios;
        const validRecent = recentPortfolios
          .filter(
            (portfolio) =>
              !!portfolios.find(
                (resource) => resource.portfolioId === portfolio.id,
              ),
          )
          .map((portfolio) => ({ ...portfolio }));

        set((state) => ({ ...state, recentPortfolios: validRecent }));

        if (recentPortfolios[0].id !== validRecent[0]?.id) {
          navigate("/portfolios");
        }
      },
    }),
    {
      name: "user-store",
      storage: createJSONStorage(() => localStorage, {
        reviver: (key, value) => {
          if (key === "date") return new Date(value as string);
          return value;
        },
      }),
    },
  ),
);
